import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import { REVENUE_SCOPE_VALUES } from '@intelligence/utils/revenueScope';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { getDeviceValuesByPageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import { GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import moment from 'moment';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { formatAppMagicGenres } from '@intelligence/v3/utils/genre';

const getCurrentFilterKeyList = (entityType: string) => {
  switch (entityType) {
    case 'console':
      return [
        filterKeyMap.FOLLOWING_GLOUP,
        filterKeyMap.REGION,
        filterKeyMap.GENRETOR,
        filterKeyMap.RANGE_TIME,
        filterKeyMap.DIGITAL_DEVICE,
      ];
    case 'company':
      return [
        filterKeyMap.FOLLOWING_GLOUP,
        filterKeyMap.REGION,
        filterKeyMap.RANGE_TIME,
      ];
    case 'mobile':
      return [
        filterKeyMap.FOLLOWING_GLOUP,
        filterKeyMap.REGION,
        filterKeyMap.GENRETOR,
        filterKeyMap.RANGE_TIME,
        filterKeyMap.REVENUE_SCOPE,
      ];
    case 'pc':
    default:
      return [
        filterKeyMap.FOLLOWING_GLOUP,
        filterKeyMap.REGION,
        filterKeyMap.GENRETOR,
        filterKeyMap.RANGE_TIME,
      ];
  }
};

const newStartData = moment().subtract(7, 'days').format('YYYY-MM-DD');
const newEndData = moment().add(12, 'M').format('YYYY-MM-DD');
export const DEFAULT_GROUPNAME = 'All Games'; //筛选默认选择分组游戏，参数字段，请不要翻译
const defaultFilterValue = {
  [filterKeyMap.REGION]: {
    regions: [],
    markets: ['global'],
    type: 'market',
  },
  [filterKeyMap.RANGE_TIME]: {
    type: 'date',
    value: ['', ''],
  },
  [filterKeyMap.DAY_RANGE_TIME]: {
    type: 'date',
    value: [newStartData, newEndData],
  },
  [filterKeyMap.GENRETOR]: {
    source: 'iegg',
    mainGenres: [],
    subGenres: [],
  },
  [filterKeyMap.FOLLOWING_GLOUP]: { value: DEFAULT_GROUPNAME },
  [filterKeyMap.DIGITAL_DEVICE]: {
    value: getDeviceValuesByPageId('DigitalRevenue'),
  },
  [filterKeyMap.REVENUE_SCOPE]: {
    value: REVENUE_SCOPE_VALUES.EXCLUDING,
  },
};

const getCurrentGamesDefaultFilterValue = (entityType: string) => {
  if (entityType !== 'mobile') {
    return defaultFilterValue;
  }

  return {
    ...defaultFilterValue,
    [filterKeyMap.GENRETOR]: {
      ...defaultFilterValue[filterKeyMap.GENRETOR],
      source: 'sensortower',
    },
  };
};

export const currentGamesFilterCfg = (params: {
  entityType: string;
  // defaultGroupValue: string;
  regionOptions: any[] | null;
  groupOptionsData: any[];
}): TopchartFilterCfgType => {
  const { entityType, regionOptions, groupOptionsData } = params || {};
  const currentGamesDefaultFilterValue = getCurrentGamesDefaultFilterValue(entityType);
  const mobileSpecialParams = (() => {
    if (entityType !== 'mobile') {
      return {};
    }

    return {
      [filterKeyMap.REVENUE_SCOPE]: {
        controlType: 'switch',
      },
      isStdGenreValuesHandler: true,
      genreSources: GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE,
      genreOverlayClassName: 'cascade-mode',
    };
  })();

  return {
    filterKeyList: getCurrentFilterKeyList(entityType),
    companyFilterParams: {},
    defaultValue: currentGamesDefaultFilterValue,
    resetFilterValue: {
      ...currentGamesDefaultFilterValue,
    },
    specialParams: {
      ...mobileSpecialParams,
      regionOptions, //国家筛选器options,
      defalutMarket: ['global'], //没取到交集用的默认值
      disableNoChina: entityType === 'pc',
      rangeTimeType: FilterTimeType.commonRange,
      groupOptionsData: groupOptionsData,
      disableIegg: false,
      disableAnnie: entityType !== 'mobile',
      disableSensorTower: entityType !== 'mobile',
      entityType
    },
    showConfirmButton: false,
    haveFilterSaveBtn: false,
    handleFilterToApiParams: (filterParams: any) => {
      return handleCurrentGameFilterParamsToApi(entityType, filterParams);
    },
  };
};

export const newGamesFilterCfg = (params: {
  entityType: string;
  // defaultGroupValue: string;
  regionOptions: any[] | null;
  groupOptionsData: any[];
}): TopchartFilterCfgType => {
  const { entityType, regionOptions, groupOptionsData } = params || {};

  return {
    filterKeyList: [
      filterKeyMap.FOLLOWING_GLOUP,
      filterKeyMap.REGION,
      filterKeyMap.DAY_RANGE_TIME,
    ],
    companyFilterParams: {},
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.DAY_RANGE_TIME]: {
        type: 'date',
        value: [newStartData, newEndData],
      },
    },
    specialParams: {
      regionOptions, //国家筛选器options,
      defalutMarket: ['global'], //没取到交集用的默认值
      disableNoChina: false,
      rangeTimeType: FilterTimeType.rangeDay,
      groupOptionsData: groupOptionsData,
      entityType
    },
    showConfirmButton: false,
    haveFilterSaveBtn: false,
    handleFilterToApiParams: handleNewGameFilterParamsToApi,
  };
};

const getAppannieGenreParams = (entityType: string, filterParams: any) => {
  const appannieGenre = filterParams?.appannie_genre || [];
  let genre = filterParams?.genre;
  let subGenre = filterParams?.sub_genre;

  if (entityType !== 'mobile') {
    return { genre, subGenre };
  }

  genre = appannieGenre.map((item: any) => {
    return item?.value;
  });
  subGenre = appannieGenre.reduce((acc: any[], item: any) => {
    const children = item?.children || [];
    const list = children.map((child: any) => {
      return {
        main: item?.value,
        sub: child?.value,
      };
    });
    return [...acc, ...list];
  }, []);

  return { genre, subGenre };
};

export const handleCurrentGameFilterParamsToApi = (entityType: string, filterParams: any) => {
  // console.log(filterParams);
  const appannieGenreParams = getAppannieGenreParams(entityType, filterParams);
  let filterForm: any = {
    region_type: filterParams?.region_type, //默认都是market
    regions: filterParams?.regions,
    date_type: filterParams?.date_type,
    start_date: filterParams?.start_date,
    end_date: filterParams?.end_date,

    genre: appannieGenreParams?.genre || [],
    sub_genre: appannieGenreParams?.subGenre || [],
    genre_source: filterParams?.genre_source,
    iegg_genre_req: filterParams?.iegg_genre_req,

    device: filterParams?.device,
    group_name: filterParams?.group_name,
  };
  if (entityType === 'mobile') {
    const genreSource = filterParams?.genre_source;
    const genreSourceAliasMap: IRecord<string> = {
      annie: 'appannie',
    };
    const genreSourceAlias = genreSourceAliasMap[genreSource] || genreSource || 'sensortower';
    filterForm = {
      ...filterForm,
      filter: {
        download_genre: genreSourceAlias,
        iegg_genre: filterParams?.iegg_genre,
        sensortower_genre: filterParams?.sensortower_genre,
        app_magic: {
          relation: Relation.Or,
          tags: formatAppMagicGenres(filterParams?.appmagic_genre),
        },
        appannie_genre: filterParams?.appannie_genre,
      },
    };
  }
  //过滤掉undefined的值
  for (let key in filterForm) {
    if (filterForm[key] === undefined) {
      delete filterForm[key];
    }
  }
  return filterForm;
};

export const handleNewGameFilterParamsToApi = (filterParams: any) => {
  // console.log(filterParams);
  let filterForm: any = {
    region_type: filterParams?.region_type, //默认都是market
    regions: filterParams?.regions,
    date_type: 'daily',
    start_date: filterParams?.start_date,
    end_date: filterParams?.end_date,

    group_name: filterParams?.group_name,
  };
  //过滤掉undefined的值
  for (let key in filterForm) {
    if (filterForm[key] === undefined) {
      delete filterForm[key];
    }
  }
  return filterForm;
};
